import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../../../Services/firebase-config';
import {
    ArrowLeftIcon,
    PencilIcon,
    SaveIcon,
    XIcon,
    UserGroupIcon,
    OfficeBuildingIcon,
    TagIcon,
    DocumentTextIcon,
    PhotographIcon,
    UploadIcon,
    TrashIcon
} from '@heroicons/react/outline';

const ProjectDetails = () => {
    const { projectId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [project, setProject] = useState(location.state?.project || null);
    const [editMode, setEditMode] = useState(false);
    const [loading, setLoading] = useState(!project);
    const [saving, setSaving] = useState(false);
    const [editedProject, setEditedProject] = useState({});
    const [uploading, setUploading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);

    useEffect(() => {
        const fetchProject = async () => {
            try {
                setLoading(true);
                const projectDoc = await getDoc(doc(db, 'projects', projectId));
                if (projectDoc.exists()) {
                    const projectData = { id: projectDoc.id, ...projectDoc.data() };
                    setProject(projectData);
                    setEditedProject(projectData);
                } else {
                    console.error('Project not found');
                    navigate('/eventdashboard/projects-management');
                }
            } catch (error) {
                console.error('Error fetching project:', error);
            } finally {
                setLoading(false);
            }
        };

        if (!project && projectId) {
            fetchProject();
        }
    }, [projectId, project, navigate]);

    const handleEdit = () => {
        setEditedProject({ ...project });
        setEditMode(true);
    };

    const handleCancel = () => {
        setEditedProject({ ...project });
        setEditMode(false);
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const projectRef = doc(db, 'projects', projectId);

            // Remove the 'id' field from the update data
            const { id, ...updateData } = editedProject;

            await updateDoc(projectRef, updateData);

            setProject(editedProject);
            setEditMode(false);

            // Show success message (you can replace this with a toast notification)
            alert('Project updated successfully!');
        } catch (error) {
            console.error('Error updating project:', error);
            alert('Error updating project. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleInputChange = (field, value) => {
        setEditedProject(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleExhibitorNamesChange = (value) => {
        // Convert comma-separated string to array
        const namesArray = value.split(',').map(name => name.trim()).filter(name => name);
        setEditedProject(prev => ({
            ...prev,
            exhibitorNames: namesArray
        }));
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                alert('Please select an image file');
                return;
            }
            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                alert('File size must be less than 5MB');
                return;
            }
            setSelectedFile(file);
        }
    };

    const handleImageUpload = async () => {
        if (!selectedFile) return;

        try {
            setUploading(true);

            // Create a unique filename
            const timestamp = Date.now();
            const filename = `projects/${projectId}_${timestamp}_${selectedFile.name}`;
            const storageRef = ref(storage, filename);

            // Upload the file
            const snapshot = await uploadBytes(storageRef, selectedFile);
            const downloadURL = await getDownloadURL(snapshot.ref);

            // Update the edited project with new image URL
            setEditedProject(prev => ({
                ...prev,
                image: downloadURL
            }));

            setSelectedFile(null);
            alert('Image uploaded successfully!');
        } catch (error) {
            console.error('Error uploading image:', error);
            alert('Error uploading image. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const handleRemoveImage = () => {
        setEditedProject(prev => ({
            ...prev,
            image: ''
        }));
        setSelectedFile(null);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex justify-center items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-blue-1"></div>
            </div>
        );
    }

    if (!project) {
        return (
            <div className="min-h-screen bg-gray-50 flex justify-center items-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Project Not Found</h2>
                    <p className="text-gray-600 mb-4">The requested project could not be found.</p>
                    <button
                        onClick={() => navigate('/eventdashboard/projects-management')}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-custom-blue-1 hover:bg-blue-700"
                    >
                        <ArrowLeftIcon className="h-4 w-4 mr-2" />
                        Back to Projects
                    </button>
                </div>
            </div>
        );
    }

    const currentProject = editMode ? editedProject : project;

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={() => navigate('/eventdashboard/projects-management')}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                            <ArrowLeftIcon className="h-4 w-4 mr-2" />
                            Back to Projects
                        </button>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Project Details</h1>
                            <p className="text-gray-600">Project ID: {project.id}</p>
                        </div>
                    </div>
                    <div className="flex items-center space-x-3">
                        {!editMode ? (
                            <button
                                onClick={handleEdit}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-custom-blue-1 hover:bg-blue-700"
                            >
                                <PencilIcon className="h-4 w-4 mr-2" />
                                Edit Project
                            </button>
                        ) : (
                            <div className="flex space-x-2">
                                <button
                                    onClick={handleCancel}
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                >
                                    <XIcon className="h-4 w-4 mr-2" />
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                                >
                                    {saving ? (
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    ) : (
                                        <SaveIcon className="h-4 w-4 mr-2" />
                                    )}
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Basic Information */}
                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <DocumentTextIcon className="h-5 w-5 mr-2 text-custom-blue-1" />
                            Basic Information
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Project ID
                                </label>
                                <input
                                    type="text"
                                    value={currentProject.id || ''}
                                    readOnly
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Institution
                                </label>
                                {editMode ? (
                                    <input
                                        type="text"
                                        value={currentProject.institution || ''}
                                        onChange={(e) => handleInputChange('institution', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-custom-blue-1 focus:border-custom-blue-1"
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        value={currentProject.institution || 'N/A'}
                                        readOnly
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                                    />
                                )}
                            </div>
                        </div>

                        <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Category
                            </label>
                            {editMode ? (
                                <input
                                    type="text"
                                    value={currentProject.category || ''}
                                    onChange={(e) => handleInputChange('category', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-custom-blue-1 focus:border-custom-blue-1"
                                />
                            ) : (
                                <input
                                    type="text"
                                    value={currentProject.category || 'Uncategorized'}
                                    readOnly
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                                />
                            )}
                        </div>

                        <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Exhibitor Names (comma-separated)
                            </label>
                            {editMode ? (
                                <textarea
                                    value={currentProject.exhibitorNames?.join(', ') || ''}
                                    onChange={(e) => handleExhibitorNamesChange(e.target.value)}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-custom-blue-1 focus:border-custom-blue-1"
                                    placeholder="Enter exhibitor names separated by commas"
                                />
                            ) : (
                                <textarea
                                    value={currentProject.exhibitorNames?.join(', ') || 'N/A'}
                                    readOnly
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                                />
                            )}
                        </div>
                    </div>

                    {/* Innovation Description */}
                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <DocumentTextIcon className="h-5 w-5 mr-2 text-custom-blue-1" />
                            Innovation Description
                        </h2>
                        {editMode ? (
                            <textarea
                                value={currentProject.innovationDescription || ''}
                                onChange={(e) => handleInputChange('innovationDescription', e.target.value)}
                                rows={6}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-custom-blue-1 focus:border-custom-blue-1"
                                placeholder="Describe the innovation project..."
                            />
                        ) : (
                            <div className="p-4 bg-gray-50 rounded-md">
                                <p className="text-gray-700 whitespace-pre-wrap">
                                    {currentProject.innovationDescription || 'No description available.'}
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Project Image */}
                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <PhotographIcon className="h-5 w-5 mr-2 text-custom-blue-1" />
                            Project Image
                        </h2>
                        {editMode ? (
                            <div className="space-y-4">
                                {/* Current Image */}
                                {currentProject.image && (
                                    <div className="relative">
                                        <img
                                            src={currentProject.image}
                                            alt="Project preview"
                                            className="w-full h-40 object-cover rounded-md border"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                        <button
                                            onClick={handleRemoveImage}
                                            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                                            title="Remove image"
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                )}

                                {/* File Upload */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Upload New Image
                                    </label>
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleFileSelect}
                                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-custom-blue-1 hover:file:bg-blue-100"
                                        />
                                        {selectedFile && (
                                            <button
                                                onClick={handleImageUpload}
                                                disabled={uploading}
                                                className="px-3 py-2 bg-custom-blue-1 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
                                            >
                                                {uploading ? (
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                                                ) : (
                                                    <UploadIcon className="h-4 w-4 mr-1" />
                                                )}
                                                {uploading ? 'Uploading...' : 'Upload'}
                                            </button>
                                        )}
                                    </div>
                                    {selectedFile && (
                                        <p className="text-sm text-gray-600 mt-1">
                                            Selected: {selectedFile.name}
                                        </p>
                                    )}
                                </div>

                                {/* Manual URL Input */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Or Enter Image URL
                                    </label>
                                    <input
                                        type="url"
                                        value={currentProject.image || ''}
                                        onChange={(e) => handleInputChange('image', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-custom-blue-1 focus:border-custom-blue-1"
                                        placeholder="https://example.com/image.jpg"
                                    />
                                </div>

                                {/* Preview for new image */}
                                {!currentProject.image && selectedFile && (
                                    <div className="w-full h-40 bg-gray-100 rounded-md border-2 border-dashed border-gray-300 flex items-center justify-center">
                                        <div className="text-center">
                                            <PhotographIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                            <p className="text-sm text-gray-500">Click upload to preview</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div>
                                {currentProject.image ? (
                                    <img
                                        src={currentProject.image}
                                        alt="Project"
                                        className="w-full h-40 object-cover rounded-md border"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                ) : (
                                    <div className="w-full h-40 bg-gray-100 rounded-md border-2 border-dashed border-gray-300 flex items-center justify-center">
                                        <div className="text-center">
                                            <PhotographIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                            <p className="text-sm text-gray-500">No image available</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Statistics */}
                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Project Statistics</h2>
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <UserGroupIcon className="h-5 w-5 text-gray-400 mr-2" />
                                    <span className="text-sm text-gray-600">Exhibitors</span>
                                </div>
                                <span className="text-sm font-medium text-gray-900">
                                    {currentProject.exhibitorNames?.length || 0}
                                </span>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <OfficeBuildingIcon className="h-5 w-5 text-gray-400 mr-2" />
                                    <span className="text-sm text-gray-600">Institution</span>
                                </div>
                                <span className="text-sm font-medium text-gray-900">
                                    {currentProject.institution ? '1' : '0'}
                                </span>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <TagIcon className="h-5 w-5 text-gray-400 mr-2" />
                                    <span className="text-sm text-gray-600">Category</span>
                                </div>
                                <span className="text-sm font-medium text-gray-900">
                                    {currentProject.category ? '1' : '0'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Quick Actions */}
                    {!editMode && (
                        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
                            <div className="space-y-2">
                                <button
                                    onClick={handleEdit}
                                    className="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-custom-blue-1 hover:bg-blue-700"
                                >
                                    <PencilIcon className="h-4 w-4 mr-2" />
                                    Edit Project
                                </button>
                                {currentProject.image && (
                                    <a
                                        href={currentProject.image}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                    >
                                        <PhotographIcon className="h-4 w-4 mr-2" />
                                        View Image
                                    </a>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProjectDetails;
